import React from 'react';
import PageSeo from '../components/PageSeo';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const NotFoundPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <PageSeo
        pathname="/404"
        title="Page Not Found | Diesel City"
        description="The page you are looking for does not exist. Return to Diesel City’s homepage or explore our diesel services in Johannesburg."
        noindex
      />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/assets/IMG-20250819-WA0033.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.05,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 4
                }}
              >
                <Box
                  sx={{
                    width: { xs: 120, md: 200 },
                    height: { xs: 120, md: 200 },
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(23, 28, 143, 0.3)',
                    mb: 3
                  }}
                >
                  <SearchIcon 
                    sx={{ 
                      fontSize: { xs: 60, md: 100 }, 
                      color: 'white' 
                    }} 
                  />
                </Box>
              </Box>

              <Typography
                variant={isMobile ? 'h1' : 'h1'}
                gutterBottom
                sx={{ 
                  fontWeight: 800, 
                  color: 'primary.main',
                  fontSize: { xs: '4rem', md: '6rem' },
                  lineHeight: 1,
                  mb: 2
                }}
              >
                404
              </Typography>

              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  mb: 3
                }}
              >
                Page Not Found
              </Typography>

              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ 
                  color: 'text.secondary', 
                  mb: 5,
                  maxWidth: 500,
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Oops! The page you're looking for doesn't exist. It might have been moved, 
                deleted, or you entered the wrong URL. Let's get you back on track.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Button
                  component={RouterLink}
                  to="/"
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: '0 4px 15px rgba(23, 28, 143, 0.3)',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(23, 28, 143, 0.4)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Go Home
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => window.history.back()}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    borderWidth: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Go Back
                </Button>
              </Box>

              <Box sx={{ mt: 6 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    mb: 3,
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Popular Pages
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  {[
                    { name: 'About Us', path: '/about' },
                    { name: 'Services', path: '/services' },
                    { name: 'Contact', path: '/contact' }
                  ].map((page) => (
                    <Button
                      key={page.name}
                      component={RouterLink}
                      to={page.path}
                      variant="text"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {page.name}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 500,
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 500,
                    backgroundColor: 'background.default',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                    opacity: 0.8
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/IMG-20250819-WA0032.jpg"
                    alt="404 Error"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default NotFoundPage;
